import { useEffect, useMemo, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import DrillDownDrawer from "../components/ui/DrillDownDrawer";
import WorkflowDiagram from "../components/WorkflowDiagram";
import WorkflowFlow from "../components/WorkflowFlow";
import { EmptyState, ErrorState, CardSkeleton } from "../components/ui/states";
import { useApi } from "../lib/useApi";
import { api, qs, ApiClientError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/Toast";
import type { Task, TaskStatus, TaskRecurrence, Project } from "@shared/types";

type ViewMode = "status" | "person" | "workflow" | "flow";
const VIEWS: { key: ViewMode; label: string }[] = [
  { key: "status", label: "Board" },
  { key: "person", label: "By person" },
  { key: "workflow", label: "Workflow" },
  { key: "flow", label: "Dependencies" },
];
const RECURRENCE_OPTIONS: { value: TaskRecurrence; label: string }[] = [
  { value: "once", label: "One-time" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];
// Cycle filter applied to every view (recurrence is derived from the Cycle column).
type CycleFilter = "all" | TaskRecurrence;
const CYCLE_FILTERS: { value: CycleFilter; label: string }[] = [
  { value: "all", label: "All cycles" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "once", label: "One-time" },
];

const STATUSES: { key: TaskStatus; label: string; accent: string }[] = [
  { key: "todo", label: "To do", accent: "border-t-ink-400" },
  { key: "in_progress", label: "In progress", accent: "border-t-accent" },
  { key: "review", label: "Review", accent: "border-t-warn" },
  { key: "done", label: "Done", accent: "border-t-positive" },
];

export default function Tasks() {
  const { activeClientId, isBtzl } = useAuth();
  const toast = useToast();
  const tasksState = useApi<{ tasks: Task[]; canCreate: boolean }>(`/api/tasks${qs({ clientId: activeClientId })}`);
  const projectsState = useApi<{ projects: Project[] }>(`/api/projects${qs({ clientId: activeClientId })}`);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [groupBy, setGroupBy] = useState<ViewMode>("status");
  const [cycleFilter, setCycleFilter] = useState<CycleFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [dragId, setDragId] = useState<number | null>(null);

  const visibleTasks = useMemo(
    () => (cycleFilter === "all" ? tasks : tasks.filter((t) => t.recurrence === cycleFilter)),
    [tasks, cycleFilter]
  );

  useEffect(() => {
    if (tasksState.data) setTasks(tasksState.data.tasks);
  }, [tasksState.data]);

  async function changeStatus(task: Task, status: TaskStatus) {
    if (task.status === status) return;
    const prev = tasks;
    setTasks((cur) => cur.map((t) => (t.id === task.id ? { ...t, status } : t)));
    try {
      const res = await api.patch<{ task: Task; next: Task | null }>(`/api/tasks/${task.id}`, { status });
      // Completing a recurring task spawns its next occurrence.
      if (res.next) {
        setTasks((cur) => [...cur, res.next as Task]);
        toast.success(`Next ${res.next.recurrence} task scheduled${res.next.dueDate ? ` for ${res.next.dueDate}` : ""}`);
      }
    } catch (err) {
      setTasks(prev);
      toast.error(err instanceof ApiClientError ? err.message : "Could not update task");
    }
  }

  async function deleteTask(task: Task) {
    if (!confirm(`Delete "${task.title}"? This cannot be undone.`)) return;
    const prev = tasks;
    setTasks((cur) => cur.filter((t) => t.id !== task.id));
    try {
      await api.del(`/api/tasks/${task.id}`);
    } catch (err) {
      setTasks(prev);
      toast.error(err instanceof ApiClientError ? err.message : "Could not delete task");
    }
  }

  const people = useMemo(() => {
    const set = new Set<string>();
    visibleTasks.forEach((t) => set.add(t.assigneeLabel || "Unassigned"));
    return [...set].sort();
  }, [visibleTasks]);

  if (tasksState.loading) {
    return (
      <>
        <PageHeader title="Tasks & Workflow" />
        <div className="grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <CardSkeleton key={i} rows={4} />)}</div>
      </>
    );
  }
  if (tasksState.error) {
    return (<><PageHeader title="Tasks & Workflow" /><ErrorState message={tasksState.error} onRetry={tasksState.refetch} /></>);
  }

  return (
    <>
      <PageHeader
        title="Tasks & Workflow"
        subtitle={isBtzl ? "Create, assign and move tasks across the workflow." : "Update the status of your tasks."}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={cycleFilter}
              onChange={(e) => setCycleFilter(e.target.value as CycleFilter)}
              className="rounded-lg border border-ink-300 bg-white px-3 py-1.5 text-sm focus-ring"
            >
              {CYCLE_FILTERS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <div className="flex rounded-lg border border-ink-300 bg-white p-0.5 text-sm">
              {VIEWS.map((v) => (
                <button
                  key={v.key}
                  onClick={() => setGroupBy(v.key)}
                  className={`rounded-md px-3 py-1 ${groupBy === v.key ? "bg-accent text-white" : "text-ink-600 hover:bg-ink-50"}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            {isBtzl && (
              <button onClick={() => setCreateOpen(true)} className="btn-primary">
                + New task
              </button>
            )}
          </div>
        }
      />

      {visibleTasks.length === 0 ? (
        <EmptyState
          title="No tasks"
          message={cycleFilter === "all" ? "No workflow tasks in the selected scope." : `No ${cycleFilter} tasks in the selected scope.`}
          icon="☑"
        />
      ) : groupBy === "flow" ? (
        <div className="card p-4">
          <WorkflowFlow tasks={visibleTasks} />
        </div>
      ) : groupBy === "workflow" ? (
        <div className="card p-4">
          <WorkflowDiagram tasks={visibleTasks} />
        </div>
      ) : groupBy === "status" ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {STATUSES.map((s) => {
            const colTasks = visibleTasks.filter((t) => t.status === s.key);
            return (
              <div
                key={s.key}
                onDragOver={(e) => { if (dragId != null) e.preventDefault(); }}
                onDrop={() => {
                  if (dragId != null) {
                    const t = tasks.find((x) => x.id === dragId);
                    if (t) changeStatus(t, s.key);
                    setDragId(null);
                  }
                }}
                className={`flex flex-col rounded-xl border border-t-2 border-ink-200 bg-ink-100/40 ${s.accent}`}
              >
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm font-semibold text-ink-700">{s.label}</span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-ink-500">{colTasks.length}</span>
                </div>
                <div className="flex-1 space-y-2 px-2 pb-3">
                  {colTasks.map((t) => (
                    <TaskCard
                      key={t.id}
                      task={t}
                      onStatusChange={changeStatus}
                      onDelete={isBtzl ? deleteTask : undefined}
                      onDragStart={() => setDragId(t.id)}
                      onDragEnd={() => setDragId(null)}
                    />
                  ))}
                  {colTasks.length === 0 && <p className="px-2 py-6 text-center text-xs text-ink-400">Drop tasks here</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {people.map((person) => {
            const personTasks = visibleTasks.filter((t) => (t.assigneeLabel || "Unassigned") === person);
            return (
              <div key={person} className="card p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink-900 text-[10px] font-semibold text-white">
                    {person.split(",")[0].trim()[0]?.toUpperCase()}
                  </span>
                  {person}
                  <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-500">{personTasks.length}</span>
                </h3>
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {personTasks.map((t) => (
                    <TaskCard key={t.id} task={t} onStatusChange={changeStatus} onDelete={isBtzl ? deleteTask : undefined} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isBtzl && createOpen && (
        <CreateTaskDrawer
          projects={projectsState.data?.projects ?? []}
          clientId={activeClientId ?? 1}
          onClose={() => setCreateOpen(false)}
          onCreated={(t) => {
            setTasks((cur) => [...cur, t]);
            setCreateOpen(false);
            toast.success("Task created");
          }}
        />
      )}
    </>
  );
}

function TaskCard({
  task,
  onStatusChange,
  onDelete,
  onDragStart,
  onDragEnd,
}: {
  task: Task;
  onStatusChange: (t: Task, s: TaskStatus) => void;
  onDelete?: (t: Task) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  return (
    <div
      draggable={!!onDragStart}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="card cursor-grab space-y-2 p-3 active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium leading-snug text-ink-900">{task.title}</p>
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(task);
            }}
            className="shrink-0 rounded p-0.5 text-ink-300 hover:bg-critical-50 hover:text-critical-700"
            title="Delete task"
          >
            ✕
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-500">
        {task.workflowStage && <span className="rounded bg-ink-100 px-1.5 py-0.5">{task.workflowStage}</span>}
        {task.recurrence !== "once" && (
          <span className="rounded bg-accent-50 px-1.5 py-0.5 font-medium capitalize text-accent-700">{task.recurrence}</span>
        )}
        {task.dueDate && <span className="rounded bg-ink-100 px-1.5 py-0.5">Due {task.dueDate}</span>}
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="truncate text-xs text-ink-600" title={task.assigneeLabel ?? ""}>
          {task.assigneeLabel ?? "Unassigned"}
        </span>
        <select
          value={task.status}
          onChange={(e) => onStatusChange(task, e.target.value as TaskStatus)}
          className="rounded-md border border-ink-300 bg-white px-1.5 py-0.5 text-xs focus-ring"
          onClick={(e) => e.stopPropagation()}
        >
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
    </div>
  );
}

function CreateTaskDrawer({
  projects,
  clientId,
  onClose,
  onCreated,
}: {
  projects: Project[];
  clientId: number;
  onClose: () => void;
  onCreated: (t: Task) => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState({
    title: "",
    assigneeLabel: "",
    workflowStage: "",
    projectId: "",
    dueDate: "",
    status: "todo" as TaskStatus,
    recurrence: "once" as TaskRecurrence,
  });
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      const { task } = await api.post<{ task: Task }>("/api/tasks", {
        clientId,
        title: form.title,
        assigneeLabel: form.assigneeLabel || null,
        workflowStage: form.workflowStage || null,
        projectId: form.projectId ? Number(form.projectId) : null,
        dueDate: form.dueDate || null,
        status: form.status,
        recurrence: form.recurrence,
      });
      onCreated(task);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not create task");
    } finally {
      setSubmitting(false);
    }
  }

  const field = (label: string, node: React.ReactNode) => (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink-700">{label}</span>
      {node}
    </label>
  );

  return (
    <DrillDownDrawer
      open
      title="New task"
      subtitle="Create and assign a workflow task"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={submitting} className="btn-primary">{submitting ? "Creating…" : "Create task"}</button>
        </div>
      }
    >
      <div className="space-y-4">
        {field("Title", <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Reconcile weekly receipts" />)}
        {field("Assignee", <input className="input" value={form.assigneeLabel} onChange={(e) => setForm({ ...form, assigneeLabel: e.target.value })} placeholder="e.g. Andreina" />)}
        {field("Workflow stage", <input className="input" value={form.workflowStage} onChange={(e) => setForm({ ...form, workflowStage: e.target.value })} placeholder="e.g. Processing" />)}
        {field("Project", (
          <select className="input" value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
            <option value="">— None —</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ))}
        {field("Due date", <input type="date" className="input" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />)}
        {field("Recurrence", (
          <select
            className="input"
            value={form.recurrence}
            onChange={(e) => setForm({ ...form, recurrence: e.target.value as TaskRecurrence })}
          >
            {RECURRENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ))}
        {field("Status", (
          <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as TaskStatus })}>
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        ))}
      </div>
    </DrillDownDrawer>
  );
}
